import React from "react";
import { Button, Input, Rate } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AdvisorIcon from "./AdvisorIcon";
import { RATING_COMMENT_MAX } from "util/advisorSessionRating";
import styles from "./style.module.scss";

/**
 * End-of-chat rating card: 5 stars, then an optional comment. GlobalCopilotPanel
 * owns the state so a half-filled card survives the next question.
 * status: "ask" | "sending" | "error" | "thanks"
 */
const AdvisorRatingCard = ({
  stars = 0,
  comment = "",
  status = "ask",
  onStarsChange,
  onCommentChange,
  onSubmit,
  onSkip,
}) => {
  const { t } = useTranslation();
  const sending = status === "sending";
  const starLabels = [1, 2, 3, 4, 5].map((n) => t(`cx_gpt.rating_star_${n}`));

  return (
    <div className={styles.messageAssistant}>
      <div className={styles.messageRow}>
        <span className={styles.messageAvatar} aria-hidden>
          <AdvisorIcon size="md" />
        </span>
        <div className={styles.messageBody}>
          {status === "thanks" ? (
            <div className={styles.ratingThanks} role="status">
              <CheckCircleFilled aria-hidden />
              <span>{t("cx_gpt.rating_thanks")}</span>
            </div>
          ) : (
            <section className={styles.ratingCard} aria-label={t("cx_gpt.rating_title")}>
              <div className={styles.ratingTitle}>{t("cx_gpt.rating_title")}</div>
              <div className={styles.ratingHint}>{t("cx_gpt.rating_hint")}</div>
              <Rate
                className={styles.ratingStars}
                value={stars}
                allowClear={false}
                disabled={sending}
                tooltips={starLabels}
                aria-label={t("cx_gpt.rating_stars_label")}
                onChange={(value) => onStarsChange(value)}
              />
              {stars > 0 ? (
                <Input.TextArea
                  className={styles.ratingComment}
                  value={comment}
                  maxLength={RATING_COMMENT_MAX}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  disabled={sending}
                  placeholder={t("cx_gpt.rating_comment_placeholder")}
                  aria-label={t("cx_gpt.rating_comment_placeholder")}
                  onChange={(e) => onCommentChange(e.target.value)}
                />
              ) : null}
              {status === "error" ? (
                <div className={styles.ratingError} role="alert">
                  {t("cx_gpt.rating_error")}
                </div>
              ) : null}
              <div className={styles.ratingActions}>
                <Button type="text" size="small" disabled={sending} onClick={onSkip}>
                  {t("cx_gpt.rating_skip")}
                </Button>
                {stars > 0 ? (
                  <Button type="primary" size="small" loading={sending} onClick={onSubmit}>
                    {t("cx_gpt.rating_send")}
                  </Button>
                ) : null}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvisorRatingCard;
